import { Link, useFetcher } from '@remix-run/react'
import { Checkbox, FormInput, FormTextArea, Heading, Tabs } from '~/components'
import type { SanityFile, SanityImage } from '~/types'
import { useForms } from '~/hooks'
import clsx from 'clsx'
import { action } from '~/routes/api.form-submit'

interface BlockMainFormProps {
  title: string
  image: SanityImage
  file?: SanityFile
}

// Main component
export default function BlockMainForm({
  title,
  image,
  file,
}: BlockMainFormProps) {
  const fetcher = useFetcher<typeof action>()
  const {
    errors,
    response,
    handleSubmit,
    activeFormTab,
    fields,
    handleChange,
    submitButton,
  } = useForms(file?.asset.url)

  return (
    <>
      <div id="form">
        <div className="absolute inset-0 -z-10 after:absolute after:inset-0 after:left-0 after:top-0 after:z-0 after:size-full after:bg-teal-800/40">
          <div className="h-full w-full">
            {image && (
              <img
                className="size-full object-cover object-center"
                src={image?.asset?.url}
                alt={image?.alt}
              />
            )}
          </div>
        </div>
        <div className="container z-10">
          <Heading
            tag="h2"
            size="h2"
            bold={false}
            className="mx-auto text-center uppercase text-beige-200 lg:w-4/5"
          >
            {title}
          </Heading>
          <div className="mx-auto bg-beige-200 p-8 lg:w-1/2">
            <Tabs
              activeTab={activeFormTab}
              tabItems={[
                {
                  id: 0,
                  title: 'Get in Touch',
                  content: (
                    <fetcher.Form
                      onSubmit={e => {
                        e.preventDefault
                        handleSubmit(fields, 'getInTouch')
                      }}
                      className="grid gap-2"
                    >
                      {response && (
                        <p
                          className={clsx({
                            'text-red-500': response.type == 'error',
                            'text-green-500': response.type == 'success',
                            'text-blue-500': response.type == 'loading',
                          })}
                        >
                          {response.message}
                        </p>
                      )}
                      <div className="grid grid-cols-1 gap-2 lg:grid-cols-2 lg:gap-4">
                        <FormInput
                          id="mce-FNAME"
                          name="firstName"
                          type="text"
                          placeholder="First name"
                          autocomplete="first name"
                          ariaLabel="first name"
                          inputMode="text"
                          error={errors.firstName}
                          onChange={e => handleChange('firstName', e)}
                          value={fields.firstName}
                        />
                        <FormInput
                          id="mce-LNAME"
                          name="lastName"
                          type="text"
                          placeholder="Last name"
                          autocomplete="last name"
                          ariaLabel="last name"
                          inputMode="text"
                          error={errors.lastName}
                          onChange={e => handleChange('lastName', e)}
                          value={fields.lastName}
                        />
                      </div>
                      <FormInput
                        id="mce-EMAIL"
                        name="email"
                        type="email"
                        placeholder="Email address"
                        autocomplete="email"
                        ariaLabel="email"
                        inputMode="email"
                        error={errors.email}
                        onChange={e => handleChange('email', e)}
                        value={fields.email}
                      />
                      <FormInput
                        id="mce-PHONE"
                        name="tel"
                        type="tel"
                        placeholder="Phone number"
                        autocomplete="tel"
                        ariaLabel="tel"
                        inputMode="tel"
                        error={errors.phone}
                        onChange={e => handleChange('phone', e)}
                        value={fields.phone}
                      />
                      <div className="my-2">
                        <p className="font-bold">Are you a travel agent?</p>
                        <Checkbox
                          id="travelAgent"
                          label="Yes"
                          checked={fields.travelAgent}
                          onChange={() =>
                            handleChange('travelAgent', !fields.travelAgent)
                          }
                        />
                      </div>
                      <div
                        className={clsx({
                          hidden: !fields.travelAgent,
                        })}
                      >
                        <FormInput
                          id="mce-ABTA"
                          name="abtaNumber"
                          type="text"
                          placeholder="What's your ABTA number?"
                          inputMode="text"
                          error={errors.abta}
                          onChange={e => handleChange('abta', e)}
                          value={fields.abta}
                        />
                      </div>
                      <FormTextArea
                        id="mce-PLANS"
                        name="plans"
                        placeholder="Tell us about your plans..."
                        error={errors.plans}
                        onChange={e => handleChange('plans', e)}
                        value={fields.plans}
                      />
                      <p className="text-xs">
                        This site is protected by reCAPTCHA and the Google{' '}
                        <Link
                          className="underline"
                          to="https://policies.google.com/privacy"
                        >
                          Privacy Policy
                        </Link>{' '}
                        and{' '}
                        <Link
                          className="underline"
                          to="https://policies.google.com/terms"
                        >
                          Terms of Service
                        </Link>{' '}
                        apply.
                      </p>
                      <div className="mx-auto">
                        <input
                          id="formSubmitBtn"
                          className="border border-transparent bg-gold-500 px-8 py-3 text-center text-beige-200 hover:border-gold-300 hover:bg-gold-300 hover:text-beige-200 disabled:opacity-50"
                          type="submit"
                          disabled={submitButton == 'Sending...'}
                          value={submitButton}
                        />
                      </div>
                    </fetcher.Form>
                  ),
                },
                {
                  id: 1,
                  title: 'Download brochure',
                  content: (
                    <fetcher.Form
                      onSubmit={e => {
                        e.preventDefault
                        handleSubmit(fields, 'brouchureForm', true)
                      }}
                      className="grid gap-2"
                    >
                      {response && (
                        <p
                          className={clsx({
                            'text-red-500': response.type == 'error',
                            'text-green-500': response.type == 'success',
                            'text-blue-500': response.type == 'loading',
                          })}
                        >
                          {response.message}
                        </p>
                      )}
                      <div className="grid grid-cols-1 gap-2 lg:grid-cols-2 lg:gap-4">
                        <FormInput
                          id="mce-FNAME"
                          name="firstName"
                          type="text"
                          placeholder="First name"
                          autocomplete="first name"
                          ariaLabel="first name"
                          inputMode="text"
                          error={errors.firstName}
                          onChange={e => handleChange('firstName', e)}
                          value={fields.firstName}
                        />
                        <FormInput
                          id="mce-LNAME"
                          name="lastName"
                          type="text"
                          placeholder="Last name"
                          autocomplete="last name"
                          ariaLabel="last name"
                          inputMode="text"
                          error={errors.lastName}
                          onChange={e => handleChange('lastName', e)}
                          value={fields.lastName}
                        />
                      </div>
                      <FormInput
                        id="mce-EMAIL"
                        name="email"
                        type="email"
                        placeholder="Email address"
                        autocomplete="email"
                        ariaLabel="email"
                        inputMode="email"
                        error={errors.email}
                        onChange={e => handleChange('email', e)}
                        value={fields.email}
                      />
                      <FormInput
                        id="mce-PHONE"
                        name="tel"
                        type="tel"
                        placeholder="Phone number"
                        autocomplete="tel"
                        ariaLabel="tel"
                        inputMode="tel"
                        error={errors.phone}
                        onChange={e => handleChange('phone', e)}
                        value={fields.phone}
                      />
                      <div className="my-2">
                        <p className="font-bold">Are you a travel agent?</p>
                        <Checkbox
                          id="travelAgent"
                          label="Yes"
                          checked={fields.travelAgent}
                          onChange={() =>
                            handleChange('travelAgent', !fields.travelAgent)
                          }
                        />
                      </div>
                      <div
                        className={clsx({
                          hidden: !fields.travelAgent,
                        })}
                      >
                        <FormInput
                          id="mce-ABTA"
                          name="abtaNumber"
                          type="text"
                          placeholder="What's your ABTA number?"
                          inputMode="text"
                          error={errors.abta}
                          onChange={e => handleChange('abta', e)}
                          value={fields.abta}
                        />
                      </div>
                      <FormTextArea
                        id="mce-PLANS"
                        name="plans"
                        placeholder="Tell us about your plans..."
                        error={errors.plans}
                        onChange={e => handleChange('plans', e)}
                        value={fields.plans}
                      />
                      <div className="my-2">
                        <p className="font-bold">
                          Would you like a printed copy of the brochure?
                        </p>
                        <Checkbox
                          id="preOrder"
                          label="Yes, I would like a printed copy of the brochure"
                          checked={fields.preOrder}
                          onChange={() =>
                            handleChange('preOrder', !fields.preOrder)
                          }
                        />
                      </div>
                      <div
                        className={clsx('grid gap-2 lg:gap-4', {
                          hidden: !fields.preOrder,
                        })}
                      >
                        <FormInput
                          id="mce-ADDRESSLINE1"
                          name="addressLine1"
                          type="text"
                          placeholder="Address line 1"
                          inputMode="text"
                          error={errors.addressLine1}
                          onChange={e => handleChange('addressLine1', e)}
                          value={fields.addressLine1}
                        />
                        <FormInput
                          id="mce-ADDRESSLINE2"
                          name="addressLine2"
                          type="text"
                          placeholder="Address line 2"
                          inputMode="text"
                          error={errors.addressLine2}
                          onChange={e => handleChange('addressLine2', e)}
                          value={fields.addressLine2}
                        />
                        <div className="grid grid-cols-1 gap-2 lg:grid-cols-2 lg:gap-4">
                          <FormInput
                            id="mce-CITY"
                            name="City"
                            type="text"
                            placeholder="Town / City"
                            inputMode="text"
                            error={errors.city}
                            onChange={e => handleChange('city', e)}
                            value={fields.city}
                          />
                          <FormInput
                            id="mce-POSTCODE"
                            name="postcode"
                            type="text"
                            placeholder="Postcode"
                            inputMode="text"
                            error={errors.postcode}
                            onChange={e => handleChange('postcode', e)}
                            value={fields.postcode}
                          />
                        </div>
                        <FormInput
                          id="mce-COPIES"
                          name="copies"
                          type="number"
                          placeholder="How many copies would you like?"
                          inputMode="numeric"
                          error={errors.copies}
                          onChange={e => handleChange('copies', e)}
                          value={fields.copies}
                        />
                      </div>
                      <div className="my-2">
                        <p className="font-bold">
                          Would you like to sign up to our Newsletter?
                        </p>
                        <Checkbox
                          id="newsletterSignup"
                          label="Yes"
                          checked={fields.newsletterSignup}
                          onChange={() =>
                            handleChange(
                              'newsletterSignup',
                              !fields.newsletterSignup
                            )
                          }
                        />
                      </div>
                      <p className="text-xs">
                        This site is protected by reCAPTCHA and the Google{' '}
                        <Link
                          className="underline"
                          to="https://policies.google.com/privacy"
                        >
                          Privacy Policy
                        </Link>{' '}
                        and{' '}
                        <Link
                          className="underline"
                          to="https://policies.google.com/terms"
                        >
                          Terms of Service
                        </Link>{' '}
                        apply.
                      </p>
                      <div className="mx-auto">
                        <input
                          id="formSubmitBtn"
                          className="border border-transparent bg-gold-500 px-8 py-3 text-center text-beige-200 hover:border-gold-300 hover:bg-gold-300 hover:text-beige-200 disabled:opacity-50"
                          type="submit"
                          disabled={submitButton == 'Sending...'}
                          value={submitButton}
                        />
                      </div>
                    </fetcher.Form>
                  ),
                },
              ]}
            />
          </div>
        </div>
      </div>
    </>
  )
}
